import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Image, rem, useUserColorMode } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import darkLogo from '../../../assets/images/bonusly-logo-dark-mode.png';
import logo from '../../../assets/images/bonusly-logo.png';
import { useSeasonalLogo } from '../../../components/seasonal/use-seasonal-logo';

type LogoRenderContext = 'default' | 'mobile' | 'sidebar';

interface CompanyLogoProps {
	context?: LogoRenderContext;
}

export const CompanyLogo: FC<CompanyLogoProps> = ({ context = 'default' }) => {
	const company = useCurrentCompany();
	const { t } = useTranslation('navigation');
	const { logoUrl: seasonalLogo } = useSeasonalLogo();
	const { isDarkMode } = useUserColorMode();

	// Determine which logo to use: seasonal > company > default (with dark mode support)
	const logoConfig = useMemo(() => {
		// Priority: seasonal theme > company logo > bonusly logo (light/dark)
		const defaultLogo = isDarkMode ? darkLogo : logo;
		const logoSrc = seasonalLogo || company?.logo_url || defaultLogo;
		const logoAlt = seasonalLogo
			? t('links.company_logo', { name: 'Bonusly 🎃' })
			: company?.logo_url
				? t('links.company_logo', { name: company.name })
				: t('links.company_logo', { name: 'Bonusly' });

		return { logoSrc, logoAlt };
	}, [seasonalLogo, company?.logo_url, company?.name, t, isDarkMode]);
	const isLeftAlignedContext = context !== 'default';
	const sidebarInset = context === 'sidebar' ? { px: rem(10), py: rem(6) } : { px: 0, py: 0 };

	return (
		<Box h="100%" w="100%" px={sidebarInset.px} py={sidebarInset.py} boxSizing="border-box">
			<Image
				alt={logoConfig.logoAlt}
				display="block"
				h="100%"
				w="100%"
				maxH="100%"
				maxW="100%"
				objectFit="contain"
				objectPosition={isLeftAlignedContext ? 'left center' : 'center center'}
				src={logoConfig.logoSrc}
			/>
		</Box>
	);
};
